import { AlertLevel, type ToastData } from '@vizit/atoms';
import { plural } from '@vizit/utils';

/*
 * What the collection page says about scoring, in one place: the toasts when a scoring starts and lands, also for
 * uploads. The upload dialog's note is `UploadScoringNotice` in organisms. The inline
 * "Scoring against {category}… N of M" status is `ScoringStatus` in organisms, shared with the benchmark page.
 */

type Toast = Omit<ToastData, 'id'>;

/** How long a "ready" toast stays up, so there is time to press "View scores". */
const SCORING_READY_TOAST_MS = 8000;
/** How long the "new assets scored" toast stays up. */
const UPLOAD_SCORED_TOAST_MS = 6000;

const TAKES_A_FEW_MINUTES = "This takes a few minutes. We'll let you know when it's ready.";

/** Scoring started for the categories just added. */
function scoringStartedToast(categoryNames: string[]): Toast {
  return {
    title:
      categoryNames.length === 1
        ? `Scoring against ${categoryNames[0]}`
        : `Scoring against ${categoryNames.length} categories`,
    description: TAKES_A_FEW_MINUTES,
    level: AlertLevel.Info,
  };
}

/** A category's scoring landed; "View scores" switches the grid to it. */
function scoringReadyToast(categoryName: string, onViewScores: () => void): Toast {
  return {
    title: `${categoryName} is ready`,
    level: AlertLevel.Success,
    timeout: SCORING_READY_TOAST_MS,
    action: { label: 'View scores', onPress: onViewScores },
  };
}

/** New assets were uploaded into a collection with categories to score them under. */
function uploadScoringToast(assetCount: number): Toast {
  return {
    title: `Scoring ${plural(assetCount, 'new asset')}`,
    description: TAKES_A_FEW_MINUTES,
    level: AlertLevel.Info,
  };
}

/** The scorings an upload reopened have all landed. */
function uploadScoredToast(assetCount: number): Toast {
  return {
    title: `${plural(assetCount, 'new asset')} scored`,
    level: AlertLevel.Success,
    timeout: UPLOAD_SCORED_TOAST_MS,
  };
}

const IMAGE_TYPE_NAMES = { hero: 'hero', carousel: 'carousel' } as const;

/**
 * Why categories could not be added under a benchmark, one sentence per refusal. The API's `error_code` tells the
 * permanent refusals (the category cannot be scored against this benchmark, so trying again will not help) from
 * the ones worth retrying.
 */
function addCategoriesErrorMessage(
  failures: { name: string; errorCode?: string }[],
  benchmark: { name: string; isVizit: boolean },
): string {
  const retry: string[] = [];
  const sentences = failures.flatMap(({ name, errorCode }) => {
    switch (errorCode) {
      case 'RETAILER_MISMATCH':
        return [`${benchmark.name} can't score ${name}: it isn't one of its categories.`];
      case 'BENCHMARK_NOT_FOUND':
        return benchmark.isVizit
          ? [`There's no ${benchmark.name} benchmark for ${name} yet.`]
          : [`${benchmark.name} no longer exists.`];
      case 'CATEGORY_HAS_NO_AUDIENCES':
        return [`${name} can't be scored yet: it has no audiences.`];
      case 'BENCHMARK_TOO_SMALL':
        return [`${benchmark.name} needs more images before it can score.`];
      case 'BENCHMARK_SCOPE_MISMATCH':
        return [`${benchmark.name} is for another image type than this collection.`];
      default:
        retry.push(name);
        return [];
    }
  });
  const unique = [...new Set(sentences)];
  if (retry.length > 0) {
    unique.push(`Couldn't add ${formatList(retry)}. Try again.`);
  }
  return unique.join(' ');
}

/** "A", "A and B", "A, B and C". */
function formatList(items: string[]) {
  if (items.length <= 1) {
    return items[0] ?? '';
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * A benchmark was just created from the collection page. It starts empty, so it cannot score yet: the toast says
 * how many images it needs, or, when its image type is not the collection's, that it will not be offered here.
 */
function benchmarkCreatedToast(
  benchmark: { name: string; scope: keyof typeof IMAGE_TYPE_NAMES },
  collection: { imageType: keyof typeof IMAGE_TYPE_NAMES; minimumImages: number },
  onAddImages: () => void,
): Toast {
  const matches = benchmark.scope === collection.imageType;
  return {
    title: `${benchmark.name} created`,
    description: matches
      ? `Add at least ${plural(collection.minimumImages, 'image')} to score this collection against it.`
      : `It's a ${IMAGE_TYPE_NAMES[benchmark.scope]} benchmark, so it isn't offered for this ${IMAGE_TYPE_NAMES[collection.imageType]} collection.`,
    level: AlertLevel.Success,
    timeout: SCORING_READY_TOAST_MS,
    action: { label: 'Add images', onPress: onAddImages },
  };
}

export {
  SCORING_READY_TOAST_MS,
  addCategoriesErrorMessage,
  UPLOAD_SCORED_TOAST_MS,
  benchmarkCreatedToast,
  scoringReadyToast,
  scoringStartedToast,
  uploadScoredToast,
  uploadScoringToast,
};
